import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiService } from "../../services/api";
import { useAuth } from "../../contexts/AuthContext";
import { clearActivePresenceSummary, getModuleKeyFromPathname, normalizeActivePresenceSummary, storeActivePresenceSummary } from "../../utils/timeTracking";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const postKeepaliveClose = (payload: any) => {
  try {
    const token = localStorage.getItem("authToken");
    if (!token || !API_BASE_URL) return;
    fetch(`${API_BASE_URL}/time-tracking/presence/close`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  } catch {}
};

export default function ActivityTracker() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const sessionIdRef = useRef<string | null>(null);
  const lastInteractionRef = useRef<number>(Date.now());
  const pathnameRef = useRef<string>(location.pathname);
  const moduleKeyRef = useRef<string>(getModuleKeyFromPathname(location.pathname));
  const pendingHeartbeatRef = useRef(false);
  const paywallRedirectedRef = useRef(false);
  const paymentAccessRef = useRef<any>(user?.paymentAccess);

  useEffect(() => {
    paymentAccessRef.current = user?.paymentAccess;
  }, [user?.paymentAccess]);

  // Deneme süresi dolduysa ve kullanıcı ücretli bir modül sayfasındaysa ödeme ekranına yönlendir.
  const handleTrialFromHeartbeat = (trial: any) => {
    if (!trial?.expired) return;
    if (paywallRedirectedRef.current) return;
    const path = pathnameRef.current || "";
    if (path.startsWith("/payment")) return; // zaten ödeme ekranında
    // Sadece ücretli (TRIAL/PAYMENT_REQUIRED) bir modül sayfasındayken paywall'a at
    const moduleKey = moduleKeyRef.current;
    const access = (paymentAccessRef.current?.modules || []).find((m: any) => m.id === moduleKey)?.access;
    if (access !== "TRIAL" && access !== "PAYMENT_REQUIRED") return;
    paywallRedirectedRef.current = true;
    navigate(`/payment?module=${encodeURIComponent(moduleKey)}&returnTo=${encodeURIComponent(path)}`);
  };

  useEffect(() => {
    pathnameRef.current = location.pathname;
    moduleKeyRef.current = getModuleKeyFromPathname(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      sessionIdRef.current = null;
      clearActivePresenceSummary();
      return;
    }

    const sendHeartbeat = async () => {
      if (pendingHeartbeatRef.current) return;
      pendingHeartbeatRef.current = true;
      try {
        const isVisible = !document.hidden;
        const isInteractive = Date.now() - lastInteractionRef.current < 60000;
        const response = await ApiService.heartbeatPresence({
          sessionId: sessionIdRef.current,
          moduleKey: moduleKeyRef.current,
          pathname: pathnameRef.current,
          isVisible,
          isInteractive,
          metadata: {
            title: document.title,
          },
        });
        sessionIdRef.current = response?.sessionId || null;
        storeActivePresenceSummary(
          normalizeActivePresenceSummary({
            ...response,
            moduleKey: moduleKeyRef.current,
            pathname: pathnameRef.current,
          })
        );
        handleTrialFromHeartbeat(response?.trial);
      } catch {
        pendingHeartbeatRef.current = false;
        return;
      }
      pendingHeartbeatRef.current = false;
    };

    const closeCurrent = async (reason: "idle_closed" | "hidden_closed" | "route_changed" | "logout_closed" | "stale_closed") => {
      if (!sessionIdRef.current) return;
      try {
        await ApiService.closePresenceSession({
          sessionId: sessionIdRef.current,
          moduleKey: moduleKeyRef.current,
          pathname: pathnameRef.current,
          reason,
          metadata: {
            title: document.title,
          },
        });
      } catch {}
      sessionIdRef.current = null;
      clearActivePresenceSummary();
    };

    const markInteraction = () => {
      lastInteractionRef.current = Date.now();
    };

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        await closeCurrent("hidden_closed");
      } else {
        lastInteractionRef.current = Date.now();
        await sendHeartbeat();
      }
    };

    const handleBeforeUnload = () => {
      if (!sessionIdRef.current) return;
      postKeepaliveClose({
        sessionId: sessionIdRef.current,
        moduleKey: moduleKeyRef.current,
        pathname: pathnameRef.current,
        reason: "hidden_closed",
        metadata: {
          title: document.title,
        },
      });
    };

    const heartbeatInterval = window.setInterval(() => {
      void sendHeartbeat();
    }, 30000);

    const interactionEvents: Array<keyof WindowEventMap> = ["mousemove", "keydown", "pointerdown", "scroll"];

    interactionEvents.forEach((eventName) => {
      window.addEventListener(eventName, markInteraction, { passive: true });
    });

    const handleVisibilityChangeEvent = () => {
      void handleVisibilityChange();
    };

    document.addEventListener("visibilitychange", handleVisibilityChangeEvent);
    window.addEventListener("beforeunload", handleBeforeUnload);

    void sendHeartbeat();

    return () => {
      window.clearInterval(heartbeatInterval);
      interactionEvents.forEach((eventName) => {
        window.removeEventListener(eventName, markInteraction);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChangeEvent);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [isAuthenticated, user?.id]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;
    const previousPath = pathnameRef.current;
    const previousModule = moduleKeyRef.current;
    const nextPath = location.pathname;
    const nextModule = getModuleKeyFromPathname(location.pathname);

    const syncRouteChange = async () => {
      if (sessionIdRef.current && (previousPath !== nextPath || previousModule !== nextModule)) {
        try {
          await ApiService.closePresenceSession({
            sessionId: sessionIdRef.current,
            moduleKey: previousModule,
            pathname: previousPath,
            reason: "route_changed",
            metadata: {
              nextPathname: nextPath,
            },
          });
        } catch {}
        sessionIdRef.current = null;
        clearActivePresenceSummary();
      }
      pathnameRef.current = nextPath;
      moduleKeyRef.current = nextModule;
      lastInteractionRef.current = Date.now();
      try {
        const response = await ApiService.heartbeatPresence({
          sessionId: null,
          moduleKey: nextModule,
          pathname: nextPath,
          isVisible: !document.hidden,
          isInteractive: true,
          metadata: {
            title: document.title,
          },
        });
        sessionIdRef.current = response?.sessionId || null;
        storeActivePresenceSummary(
          normalizeActivePresenceSummary({
            ...response,
            moduleKey: nextModule,
            pathname: nextPath,
          })
        );
        handleTrialFromHeartbeat(response?.trial);
      } catch {}
    };

    void syncRouteChange();
  }, [location.pathname, isAuthenticated, user?.id]);

  return null;
}
